import { TextStyle } from 'react-native';

type FontStyle = Pick<TextStyle, 'fontFamily' | 'fontWeight' | 'fontStyle'>;

/**
 * I want to be able to say:
 *
 * ```
 * {
 *   fontFamily: 'foo',
 *   fontWeight: '400',
 *   fontStyle: 'italic'
 * }
 * ```
 *
 * to declare my font. Unfortunately, some of the fonts we download have the weight and style built into the font
 * family. So we actually need something like:
 *
 * ```
 * {
 *   fontFamily: 'foo-RegularItalic'
 * }
 * ```
 *
 * This function turns the former into the latter, so you can write the style using the meta-family (e.g. 'foo')
 * instead of specific font families (e.g. 'foo-Regular'). (Meta-family is a term made up for this function.)
 */
export function resolveFont<S extends FontStyle>(style: S): S {
  const fontFamily = style.fontFamily;

  // Can't do anything if they don't provide a font family.
  if (fontFamily === undefined) {
    return style;
  }

  let metaFamily = metaFamilies.find(family => family.name === fontFamily);
  if (!metaFamily) {
    // Didn't recognize the font family. Maybe it was already bundled? Try again, unbundling them
    for (const testFamily of metaFamilies) {
      const unbundled = testFamily.unbundle(fontFamily);
      if (unbundled !== null) {
        // Found one. Replace the bundled fontFamily property with the unbundled style we just found.
        metaFamily = testFamily;
        delete style.fontFamily;
        style = { ...unbundled, ...style };
        break;
      }
    }
  }

  // If we still don't recognize the font family, we can't do anything.
  if (!metaFamily) {
    return style;
  }

  const family = metaFamily.bundle(
    resolveFontWeightToNumberString(style.fontWeight),
    style.fontStyle ?? 'normal'
  );

  // Tweak the given style to use the real font.
  const newStyle = { ...style };
  newStyle.fontFamily = family;
  delete newStyle.fontWeight;
  delete newStyle.fontStyle;
  return newStyle;
}

/** Weight names as given in files */
const weightNumberToName = {
  '100': 'Thin',
  '200': 'ExtraLight',
  '300': 'Light',
  '400': 'Regular',
  '500': 'Medium',
  '600': 'SemiBold',
  '700': 'Bold',
  '800': 'ExtraBold',
  '900': 'Black'
} as const;

type WeightNumber = keyof typeof weightNumberToName;
type WeightName = (typeof weightNumberToName)[WeightNumber];
const weightNameToNumber = Object.fromEntries(
  Object.entries(weightNumberToName).map(([k, v]) => [v, k])
) as Record<WeightName, WeightNumber>;

/** Weight names as given in react-native styles */
const weightStyleNameToNumber = {
  thin: '100',
  ultralight: '200',
  light: '300',
  regular: '400',
  normal: '400',
  medium: '500',
  semibold: '600',
  bold: '700',
  heavy: '900',
  black: '900'
} as const;

type WeightStyleName = keyof typeof weightStyleNameToNumber;
const isWeightStyleName = (x: string): x is WeightStyleName => x in weightStyleNameToNumber;

// Style names
type Style = 'normal' | 'italic';

type MetaFamily = {
  name: string;
  bundle: (fontWeight: WeightNumber, fontStyle: Style) => string;
  /** Null indicates it wasn't this family. */
  unbundle: (bundledFamily: string) => { fontWeight: WeightNumber; fontStyle: Style } | null;
};

/** 'normal' is interpreted as 400 weight, 'bold' as 700 weight, etc. */
function resolveFontWeightToNumberString(weight: TextStyle['fontWeight']): WeightNumber {
  if (weight === undefined) return '400';
  if (typeof weight === 'number') return weight?.toString() as WeightNumber;
  if (isWeightStyleName(weight)) return weightStyleNameToNumber[weight];
  // We don't support these font weights
  if (weight === 'condensed') return '400';
  if (weight === 'condensedBold') return '700';
  return weight;
}

/** Mapping from family to the actual font asset files we have. */
const metaFamilies: MetaFamily[] = [
  {
    name: 'Biotif',
    bundle: (weight, style) =>
      `Biotif-${weightNumberToName[weight]}${style === 'italic' ? 'Italic' : ''}`,
    unbundle: bundledFamily => {
      const match = bundledFamily.match(/^Biotif-(.+?)(Italic)?$/);
      if (match === null) {
        return null;
      }
      const [_, weightName, style] = match;
      return {
        fontWeight: weightNameToNumber[weightName as WeightName],
        fontStyle: style ? 'italic' : 'normal'
      };
    }
  },
  {
    name: 'Roboto',
    bundle: (weight, style) =>
      `Roboto_${weight}${weightNumberToName[weight]}${style === 'italic' ? '_Italic' : ''}`,
    unbundle: bundledFamily => {
      const match = bundledFamily.match(/^Roboto_\d*(.+?)(_Italic)?$/);
      if (match === null) {
        return null;
      }
      const [_, weightName, style] = match;
      return {
        fontWeight: weightNameToNumber[weightName as WeightName],
        fontStyle: style ? 'italic' : 'normal'
      };
    }
  },
  {
    name: 'Libre Baskerville',
    bundle: (weight, style) =>
      `LibreBaskerville_${weight}${weightNumberToName[weight]}${
        style === 'italic' ? '_Italic' : ''
      }`,
    unbundle: bundledFamily => {
      const match = bundledFamily.match(/^LibreBaskerville_\d*(.+?)(_Italic)?$/);
      if (match === null) {
        return null;
      }
      const [_, weightName, style] = match;
      return {
        fontWeight: weightNameToNumber[weightName as WeightName],
        fontStyle: style ? 'italic' : 'normal'
      };
    }
  },
  {
    name: 'White_Rose_Noto',
    bundle: (weight, style) =>
      `White_Rose_Noto-${weightNumberToName[weight]}${style === 'italic' ? 'Italic' : ''}`,
    unbundle: bundledFamily => {
      const match = bundledFamily.match(/^White_Rose_Noto-(.+?)(Italic)?$/);
      if (match === null) {
        return null;
      }
      const [_, weightName, style] = match;
      return {
        fontWeight: weightNameToNumber[weightName as WeightName],
        fontStyle: style ? 'italic' : 'normal'
      };
    }
  }
];

/**
 * Normal sentence rules:
 * 1. By default, font size should be 32 with a weight of 400, i.e. not bold.
 * 2. Font sizes can be reduced to fit text, prevent awkward wrapping etc., but must never be less than 22.
 * 3. If any sentence or text contains a fraction, the font size should normally increase to 40.
 * 4. Fractions must be aligned correctly in a sentence, and with each other - taking care with answer boxes and alignment of fraction lines.
 * 5. Bold/italicised text can, and is, used sparingly in sentences to emphasise particular words or phrases in a sentence.
 */
export const sentenceStandardFontSize = 32;
export const sentenceStandardFontSizePDF = 50;
export const sentenceWithFractionsStandardFontSize = 40;

// lineHeights tend to be 1.5 x their matching font size.
export const sentenceStandardLineHeight = 48;
export const sentenceStandardLineHeightPDF = 75;
export const sentenceWithFractionsStandardLineHeight = 60;

/**
 * Draggables rules:
 * 1. By default, if draggables and sentence(s) are only numbers/not proper strings, font size of draggables should be 40 with a weight of 700, i.e. bold.
 * 2. If draggables and sentences are proper strings, i.e. they are actual phrases or sentences, font size should instead be 32 to match with instruction size.
 * 3. Draggables with fractions don't usually fit at 40, so tend to be 32 instead.
 * 4. In general, font sizes can be reduced to fit text in the draggables.
 * 5. Sentence font size should almost always be the same as the draggables - very rarely should these ever be allowed to be different.
 * 6. Sentence font weight should be the default 400, i.e. not bold, though parts with bold or italic formatting can be used (very rare).
 * 7. Action panel variant, generally speaking, should be bottom or end.
 * This isn't a hard-and-fast rule, but if draggables can fit with bottom/end instead of endWide, and the sentence/content still looks good,
 * we should use these. Generally speaking, we should use bottom over end, but this isn't a consistent rule
 * (e.g. we tend to use end for <, >, = Qs, QF37 with multiple sentences etc.).
 */
export const draggableStandardFontSize = 40;
export const draggableStandardFontSizePDF = 50;
export const draggableSentenceFontSize = 32;

// lineHeights tend to be 1.5 x their matching font size.
export const draggableStandardLineHeight = 60;
export const draggableStandardLineHeightPDF = 75;
export const draggableSentenceLineHeight = 48;

export const selectableStandardFontSize = 40;
export const selectableStandardFontSizePDF = 50;
export const selectableSentenceFontSize = 32;

// lineHeights tend to be 1.5 x their matching font size.
export const selectableStandardLineHeight = 60;
export const selectableStandardLineHeightPDF = 75;
export const selectableSentenceLineHeight = 48;
